import React, { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { 
  Package, Warehouse, ArrowUpDown, AlertTriangle, TrendingUp, TrendingDown,
  Plus, RefreshCw, Loader2, Download, Search, Filter, MoreHorizontal
} from "lucide-react";
import axios from "axios";

const Inventory = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState("stock");
  const [warehouses, setWarehouses] = useState([]);
  const [stockLevels, setStockLevels] = useState([]);
  const [movements, setMovements] = useState([]);
  const [reorderSuggestions, setReorderSuggestions] = useState([]);
  const [valuation, setValuation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWarehouseDialog, setShowWarehouseDialog] = useState(false);
  const [showMovementDialog, setShowMovementDialog] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState("all");

  // Warehouse form
  const [warehouseForm, setWarehouseForm] = useState({
    name: "", code: "", address: "", is_default: false
  });

  // Movement form
  const [movementForm, setMovementForm] = useState({
    article_id: "", warehouse_id: "", movement_type: "in",
    quantity: "", unit_cost: "", notes: ""
  });

  const [articles, setArticles] = useState([]);

  useEffect(() => {
    fetchData();
  }, [selectedWarehouse]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [warehousesRes, stockRes, movementsRes, reorderRes, articlesRes] = await Promise.all([
        axios.get(`${API}/warehouses`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/inventory/stock-levels${selectedWarehouse !== 'all' ? `?warehouse_id=${selectedWarehouse}` : ''}`, 
          { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/inventory/movements?limit=50`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/inventory/reorder-suggestions`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/articles`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setWarehouses(warehousesRes.data);
      setStockLevels(stockRes.data);
      setMovements(movementsRes.data);
      setReorderSuggestions(reorderRes.data);
      setArticles(articlesRes.data);
      
      // Set default warehouse for movement form
      if (warehousesRes.data.length > 0 && !movementForm.warehouse_id) {
        const defaultWh = warehousesRes.data.find(w => w.is_default) || warehousesRes.data[0];
        setMovementForm(prev => ({ ...prev, warehouse_id: defaultWh.id }));
      }
    } catch (error) {
      console.error('Error fetching inventory data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchValuation = async () => {
    try {
      const res = await axios.get(`${API}/inventory/valuation`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setValuation(res.data);
    } catch (error) {
      toast.error("Kunde inte hämta lagervärde");
    }
  };

  const createWarehouse = async () => {
    try {
      await axios.post(`${API}/warehouses`, warehouseForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Lagerplats skapad");
      setShowWarehouseDialog(false);
      setWarehouseForm({ name: "", code: "", address: "", is_default: false });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte skapa lagerplats");
    }
  };

  const createMovement = async () => {
    if (!movementForm.article_id || !movementForm.quantity) {
      toast.error("Fyll i artikel och antal");
      return;
    }
    
    try {
      const res = await axios.post(`${API}/inventory/movements`, {
        ...movementForm,
        quantity: parseFloat(movementForm.quantity),
        unit_cost: movementForm.unit_cost ? parseFloat(movementForm.unit_cost) : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Lagerrörelse registrerad. Nytt saldo: ${res.data.new_quantity}`);
      
      if (res.data.reorder_alert) {
        toast.warning(`Varning: ${res.data.reorder_alert.article_name || 'Artikel'} behöver beställas!`);
      }
      
      setShowMovementDialog(false);
      setMovementForm(prev => ({ ...prev, article_id: "", quantity: "", unit_cost: "", notes: "" }));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte registrera rörelse");
    }
  };

  const getMovementTypeLabel = (type) => {
    const labels = { in: "Inleverans", out: "Utleverans", adjustment: "Justering", transfer: "Överföring" };
    return labels[type] || type;
  };

  const getMovementTypeBadge = (type) => {
    if (type === "in") return <Badge className="bg-green-100 text-green-700">Inleverans</Badge>;
    if (type === "out") return <Badge className="bg-red-100 text-red-700">Utleverans</Badge>;
    if (type === "adjustment") return <Badge className="bg-blue-100 text-blue-700">Justering</Badge>;
    return <Badge variant="secondary">{type}</Badge>;
  };

  return (
    <div className="p-6 space-y-6" data-testid="inventory-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Lagerhantering</h1>
          <p className="text-muted-foreground">Hantera lager, lagersaldo och lagerrörelser</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={showWarehouseDialog} onOpenChange={setShowWarehouseDialog}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <Warehouse className="h-4 w-4 mr-2" />
                Ny lagerplats
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Skapa lagerplats</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Namn</Label>
                    <Input 
                      value={warehouseForm.name}
                      onChange={(e) => setWarehouseForm({ ...warehouseForm, name: e.target.value })}
                      placeholder="Huvudlager"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Kod</Label>
                    <Input 
                      value={warehouseForm.code}
                      onChange={(e) => setWarehouseForm({ ...warehouseForm, code: e.target.value })}
                      placeholder="HL01"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Adress</Label>
                  <Textarea 
                    value={warehouseForm.address}
                    onChange={(e) => setWarehouseForm({ ...warehouseForm, address: e.target.value })}
                    placeholder="Industrivägen 1, 123 45 Stockholm"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={warehouseForm.is_default}
                    onCheckedChange={(v) => setWarehouseForm({ ...warehouseForm, is_default: v })}
                  />
                  <Label>Standardlager</Label>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowWarehouseDialog(false)}>Avbryt</Button>
                <Button onClick={createWarehouse}>Skapa</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          <Dialog open={showMovementDialog} onOpenChange={setShowMovementDialog}>
            <DialogTrigger asChild>
              <Button>
                <ArrowUpDown className="h-4 w-4 mr-2" />
                Ny lagerrörelse
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Registrera lagerrörelse</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Artikel</Label>
                  <Select value={movementForm.article_id} onValueChange={(v) => setMovementForm({ ...movementForm, article_id: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Välj artikel" />
                    </SelectTrigger>
                    <SelectContent>
                      {articles.map(a => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.article_number} - {a.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Lagerplats</Label>
                    <Select value={movementForm.warehouse_id} onValueChange={(v) => setMovementForm({ ...movementForm, warehouse_id: v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Välj lager" />
                      </SelectTrigger>
                      <SelectContent>
                        {warehouses.map(w => (
                          <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Typ</Label>
                    <Select value={movementForm.movement_type} onValueChange={(v) => setMovementForm({ ...movementForm, movement_type: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="in">Inleverans</SelectItem>
                        <SelectItem value="out">Utleverans</SelectItem>
                        <SelectItem value="adjustment">Justering</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Antal</Label>
                    <Input 
                      type="number"
                      value={movementForm.quantity}
                      onChange={(e) => setMovementForm({ ...movementForm, quantity: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Enhetskostnad (valfritt)</Label>
                    <Input 
                      type="number"
                      value={movementForm.unit_cost}
                      onChange={(e) => setMovementForm({ ...movementForm, unit_cost: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Anteckning</Label>
                  <Textarea 
                    value={movementForm.notes}
                    onChange={(e) => setMovementForm({ ...movementForm, notes: e.target.value })}
                    placeholder="Valfri notering..."
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowMovementDialog(false)}>Avbryt</Button>
                <Button onClick={createMovement}>Registrera</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Lagerplatser</p>
                <p className="text-2xl font-bold">{warehouses.length}</p>
              </div>
              <Warehouse className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Artiklar i lager</p>
                <p className="text-2xl font-bold">{stockLevels.length}</p>
              </div>
              <Package className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Behöver beställas</p>
                <p className="text-2xl font-bold text-amber-600">{reorderSuggestions.length}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:bg-muted/50" onClick={fetchValuation}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Lagervärde</p>
                <p className="text-2xl font-bold">
                  {valuation ? `${valuation.total_value.toLocaleString('sv-SE')} kr` : 'Klicka för att beräkna'}
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Reorder Alerts */}
      {reorderSuggestions.length > 0 && (
        <Card className="border-amber-200 bg-amber-50 dark:bg-amber-950/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-amber-700 dark:text-amber-400 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Artiklar som behöver beställas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              {reorderSuggestions.map((item, idx) => (
                <div key={idx} className="p-3 bg-white dark:bg-background rounded-lg border">
                  <p className="font-medium">{item.article_name || item.article_number}</p>
                  <p className="text-sm text-muted-foreground">
                    Saldo: {item.current_quantity} (Min: {item.reorder_point})
                  </p>
                  <p className="text-sm">Föreslaget: {item.suggested_quantity} st</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex justify-between items-center">
          <TabsList>
            <TabsTrigger value="stock">Lagersaldo</TabsTrigger>
            <TabsTrigger value="movements">Lagerrörelser</TabsTrigger>
            <TabsTrigger value="warehouses">Lagerplatser</TabsTrigger>
          </TabsList>
          
          {activeTab === "stock" && (
            <Select value={selectedWarehouse} onValueChange={setSelectedWarehouse}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Alla lager" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alla lager</SelectItem>
                {warehouses.map(w => (
                  <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <TabsContent value="stock" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="p-3 text-left">Artikelnr</th>
                    <th className="p-3 text-left">Namn</th>
                    <th className="p-3 text-right">I lager</th>
                    <th className="p-3 text-right">Reserverat</th>
                    <th className="p-3 text-right">Tillgängligt</th>
                    <th className="p-3 text-right">Min.nivå</th>
                    <th className="p-3 text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {stockLevels.map(level => (
                    <tr key={level.id} className="border-t hover:bg-muted/30">
                      <td className="p-3 font-mono">{level.article_number}</td>
                      <td className="p-3">{level.article_name}</td>
                      <td className="p-3 text-right">{level.quantity}</td>
                      <td className="p-3 text-right">{level.reserved_quantity || 0}</td>
                      <td className="p-3 text-right font-semibold">{level.available_quantity}</td>
                      <td className="p-3 text-right">{level.reorder_point || '-'}</td>
                      <td className="p-3 text-center">
                        {level.needs_reorder ? (
                          <Badge variant="destructive">Beställ</Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-700">OK</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                  {stockLevels.length === 0 && (
                    <tr>
                      <td colSpan={7} className="p-8 text-center text-muted-foreground">
                        Inga artiklar i lager
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="movements" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="p-3 text-left">Datum</th>
                    <th className="p-3 text-left">Typ</th>
                    <th className="p-3 text-left">Artikel</th>
                    <th className="p-3 text-right">Antal</th>
                    <th className="p-3 text-right">Saldo efter</th>
                    <th className="p-3 text-left">Anteckning</th>
                  </tr>
                </thead>
                <tbody>
                  {movements.map(mov => (
                    <tr key={mov.id} className="border-t hover:bg-muted/30">
                      <td className="p-3">{new Date(mov.created_at).toLocaleString('sv-SE')}</td>
                      <td className="p-3">{getMovementTypeBadge(mov.movement_type)}</td>
                      <td className="p-3">{mov.article_id}</td>
                      <td className="p-3 text-right font-mono">
                        {mov.movement_type === 'out' ? '-' : '+'}{mov.quantity}
                      </td>
                      <td className="p-3 text-right font-mono">{mov.running_quantity}</td>
                      <td className="p-3 text-muted-foreground">{mov.notes || '-'}</td>
                    </tr>
                  ))}
                  {movements.length === 0 && (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-muted-foreground">
                        Inga lagerrörelser
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="warehouses" className="mt-4">
          <div className="grid grid-cols-3 gap-4">
            {warehouses.map(wh => (
              <Card key={wh.id}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{wh.name}</span>
                    {wh.is_default && <Badge>Standard</Badge>}
                  </CardTitle>
                  <CardDescription>Kod: {wh.code}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">{wh.address || 'Ingen adress angiven'}</p>
                </CardContent>
              </Card>
            ))}
            {warehouses.length === 0 && (
              <Card className="col-span-3">
                <CardContent className="py-8 text-center text-muted-foreground">
                  Inga lagerplatser skapade. Skapa din första lagerplats för att börja.
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Inventory;
